import React from "react";

/* Small reusable code block with Copy button */
function CodeBlock({ text, label = "Copy" }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="stt-code">
      <pre className="stt-pre" aria-label="code snippet">
        <code>{text}</code>
      </pre>
      <button className="stt-btn stt-btn-outline" onClick={copy}>
        {copied ? "Copied!" : label}
      </button>
    </div>
  );
}

/* Output block (visually distinct from code) */
function OutputBlock({ text, label = "Output" }) {
  return (
    <div className="stt-output">
      <div className="stt-output-label">{label}</div>
      <pre className="stt-pre" aria-label="example output">
        {text}
      </pre>
    </div>
  );
}

/* Thebe-aware cell */
function ThebeCell({ code }) {
  return (
    <pre
      data-executable
      data-language="python"
      className="stt-pre"
      style={{ whiteSpace: "pre-wrap", overflowX: "auto", maxWidth: "100%" }}
    >
      {code}
    </pre>
  );
}

const codeSnippet = `# softgraph quick start (GPU/backed env)
from softgraph import softgraph

results = softgraph(
    task="node_classification",
    model="gcn",       # or "graphsage", "tgat", "mlp"
    dataset="cora",
    plot=True
)

print(results)`;

const exampleLog = `# training log (truncated)
Epoch 1/200  | loss=1.82  | acc=0.34 | val_acc=0.41
Epoch 20/200 | loss=0.94  | acc=0.74 | val_acc=0.78
Epoch 40/200 | loss=0.67  | acc=0.82 | val_acc=0.81
...
Best @ epoch 142 | val_acc=0.84 | saved checkpoint 'gcn_cora.ckpt'`;

const exampleResults = `{'accuracy': 1.0, 'f1_macro': 1.0}
# note: your numbers will vary based on split/seed; this is a demo run`;

const colabCell = `!pip install softgraph
# then run the snippet from the Code section`;

const colabOutput = `Collecting softgraph
  Downloading softgraph-x.y.z-py3-none-any.whl ...
Collecting torch ...
Collecting torch-geometric ...
Installing collected packages: softgraph, torch, torch-geometric, ...
Successfully installed softgraph-x.y.z torch-a.b.c torch-geometric-d.e.f`;

export default function SoftgraphExamples() {
  return (
    <main className="docs-wrap">
      <div className="docs-layout">
        <aside className="docs-toc">
          <strong>On this page</strong>
          <a href="#intro">Intro</a>
          <a href="#run">Run on Colab</a>
          <a href="#code">Code</a>
          <a href="#live">Live Cell (Thebe)</a>
        </aside>

        <article>
          <header className="stt-hero">
            <div className="stt-pill">softgraph • Examples</div>
            <h1 className="stt-h1">Graph ML examples</h1>
            <p className="stt-lead">
              These require a full Python env (PyTorch). Use Colab or Binder/JupyterHub.
            </p>
          </header>

          {/* Intro */}
          <section id="intro" className="stt-section">
            <p className="stt-p">
              In-browser Python (Pyodide) can’t run PyTorch/torch-geometric.
              For softgraph training, use a remote kernel (Colab / Binder / JupyterHub).
            </p>
          </section>

          {/* Code → Output: Colab install */}
          <section id="run" className="stt-section stt-card">
            <h2 className="stt-card-title">Run on Colab</h2>
            <div className="stt-card-body">
              <CodeBlock text={colabCell} label="Copy Colab cell" />
              <OutputBlock label="Expected Colab output" text={colabOutput} />
              <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginTop: 12 }}>
                <a className="stt-btn" href="/examples/softgraph/softgraph_colab.ipynb">
                  Open local notebook
                </a>
                <a
                  className="stt-btn stt-btn-outline"
                  href="https://colab.research.google.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open on Colab
                </a>
              </div>
            </div>
          </section>

          {/* Code → Output: main example */}
          <section id="code" className="stt-section">
            <h2 className="stt-h2">Code</h2>
            <CodeBlock text={codeSnippet} label="Copy example" />
            <OutputBlock label="Sample training log" text={exampleLog} />
            <OutputBlock label="Printed results" text={exampleResults} />
          </section>

          {/* Code → Output: Thebe live cell */}
          <section id="live" className="stt-section stt-card">
            <h2 className="stt-card-title">Live Cell (via Thebe + Binder)</h2>
            <div className="stt-card-body">
              <ThebeCell code={codeSnippet} />
              <OutputBlock
                label="When you run it, you'll see…"
                text={`${exampleLog}\n\n${exampleResults}\n\n[If plot=True and your environment supports it, a training/metric plot will render in the notebook output.]`}
              />
              <p className="stt-p" style={{ opacity: 0.8 }}>
                Tip: first Binder startup can take a minute on cold boot.
              </p>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
